import { useEffect, useState } from "react";
import axios from "axios";
import { FileText, Clock, Calendar, ExternalLink, AlertTriangle, ChevronDown, MapPin, IndianRupee, Globe2 } from "lucide-react";
import { useSelectedCountry } from "@/context/SelectedCountryContext";
import Checklist from "@/pages/Checklist";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Visa() {
  const [tab, setTab] = useState("required"); // required | freedom | checklist
  return (
    <div data-testid="visa-page" className="fade-in max-w-7xl mx-auto px-6 lg:px-12 py-16">
      <div className="mb-8 max-w-2xl">
        <div className="overline mb-3">Visa Guide</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-[#0F172A]">
          Plan your visa, before your flight.
        </h1>
      </div>

      <div className="flex flex-wrap gap-2 mb-10 border-b border-[#E2E8F0]">
        <TabButton active={tab === "required"} onClick={() => setTab("required")} testId="visa-tab-required">
          Visa Requirements by Country
        </TabButton>
        <TabButton active={tab === "freedom"} onClick={() => setTab("freedom")} testId="visa-tab-freedom">
          Visa-Free Destinations
        </TabButton>
        <TabButton active={tab === "checklist"} onClick={() => setTab("checklist")} testId="visa-tab-checklist">
          Trip Checklist
        </TabButton>
      </div>

      {tab === "required" && <VisaRequirements />}
      {tab === "freedom" && <VisaFreedom />}
      {tab === "checklist" && <Checklist embed />}
    </div>
  );
}

function TabButton({ active, onClick, children, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`px-5 py-3 -mb-px text-sm font-medium border-b-2 transition-all ${
        active
          ? "border-[#002FA7] text-[#002FA7]"
          : "border-transparent text-[#475569] hover:text-[#0F172A]"
      }`}
    >
      {children}
    </button>
  );
}

function VisaRequirements() {
  const { slug } = useSelectedCountry();
  const [detail, setDetail] = useState(null);
  const [openTypeIdx, setOpenTypeIdx] = useState(0);

  useEffect(() => {
    if (!slug) return;
    setDetail(null);
    axios.get(`${API}/visa/${slug}`).then((r) => { setDetail(r.data); setOpenTypeIdx(0); });
  }, [slug]);

  if (!slug) {
    return <div data-testid="visa-no-country" className="text-[#475569]">Pick a destination first to see its visa info.</div>;
  }

  return (
    <div className="space-y-6">
        {!detail ? <div className="text-[#475569]">Loading…</div> : (
          <>
            <div className="card p-8 border-l-4 border-l-[#002FA7]">
              <div className="flex items-start gap-4 mb-4 flex-wrap">
                <span className="text-4xl">{detail.flag}</span>
                <div className="flex-1 min-w-[200px]">
                  <h2 className="font-display text-3xl font-bold tracking-tight">{detail.country}</h2>
                  <p className="mt-2 text-[#475569] leading-relaxed">{detail.summary}</p>
                </div>
                <span className={`pill ${detail.visa_required ? "pill-law" : "pill-do"}`}>
                  {detail.visa_required ? "Visa Required" : "Visa-Free / On Arrival"}
                </span>
              </div>
              <div className="grid sm:grid-cols-2 gap-4 mt-6 pt-6 border-t border-[#E2E8F0]">
                <InfoRow Icon={Clock} label="Processing Time" value={detail.processing_time} />
                <InfoRow Icon={Calendar} label="Validity" value={detail.validity} />
                <InfoRow Icon={MapPin} label="Where to Apply" value={detail.where_to_apply} />
                <InfoRow Icon={ExternalLink} label="Official Link" value={
                  <a href={detail.official_link} target="_blank" rel="noopener noreferrer" className="text-[#002FA7] hover:underline break-all">{detail.official_link}</a>
                } />
              </div>
            </div>

            <div className="card p-8">
              <div className="overline mb-4 text-[#002FA7]">Visa Types</div>
              <div className="space-y-3">
                {detail.types.map((t, i) => {
                  const open = openTypeIdx === i;
                  return (
                    <div key={i} data-testid={`visa-type-${i}`} className={`border rounded-sm transition-all ${open ? "border-[#002FA7]" : "border-[#E2E8F0]"}`}>
                      <button onClick={() => setOpenTypeIdx(open ? -1 : i)} className="w-full flex items-center justify-between p-4 text-left">
                        <div className="flex items-center gap-3">
                          <FileText className="w-4 h-4 text-[#002FA7]" strokeWidth={1.5} />
                          <span className="font-display font-semibold text-[#0F172A]">{t.name}</span>
                        </div>
                        <ChevronDown className={`w-4 h-4 text-[#475569] transition-transform ${open ? "rotate-180" : ""}`} />
                      </button>
                      {open && (
                        <div className="px-4 pb-5 pt-1 border-t border-[#E2E8F0] space-y-4">
                          <div><div className="overline mb-1">Eligibility</div><p className="text-sm text-[#0F172A] leading-relaxed">{t.eligibility}</p></div>
                          <div className="flex items-start gap-2"><IndianRupee className="w-4 h-4 text-[#002FA7] mt-0.5" strokeWidth={1.5} /><div><div className="overline mb-0.5">Fee</div><p className="text-sm text-[#0F172A]">{t.fee}</p></div></div>
                          <div>
                            <div className="overline mb-2">Documents Required</div>
                            <ul className="space-y-1.5">{t.documents.map((d, j) => (<li key={j} className="flex items-start gap-2 text-sm text-[#0F172A]"><span className="mt-1.5 w-1 h-1 rounded-full bg-[#002FA7] shrink-0" /><span className="leading-relaxed">{d}</span></li>))}</ul>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="card p-8 bg-[#FFF7ED] border-l-4 border-l-[#FF3B30]">
              <div className="flex items-center gap-2 mb-4"><AlertTriangle className="w-5 h-5 text-[#9A3412]" strokeWidth={1.8} /><div className="overline text-[#9A3412]">Important Precautions</div></div>
              <ul className="space-y-2">{detail.precautions.map((p, i) => (<li key={i} data-testid={`visa-precaution-${i}`} className="flex items-start gap-2 text-sm text-[#0F172A]"><span className="mt-1.5 w-1 h-1 rounded-full bg-[#9A3412] shrink-0" /><span className="leading-relaxed">{p}</span></li>))}</ul>
            </div>

            <div className="card p-6 bg-[#0F172A] text-white text-sm leading-relaxed">
              <strong className="font-display">Disclaimer:</strong> Visa rules change frequently. Always verify on the official embassy/consulate website before applying. This page is informational, not legal advice.
            </div>
          </>
        )}
    </div>
  );
}

function VisaFreedom() {
  const [passports, setPassports] = useState([]);
  const [code, setCode] = useState("IN");
  const [data, setData] = useState(null);

  useEffect(() => { axios.get(`${API}/visa-freedoms/passports`).then((r) => setPassports(r.data)); }, []);
  useEffect(() => { if (code) axios.get(`${API}/visa-freedoms/${code}`).then((r) => setData(r.data)); }, [code]);

  return (
    <div data-testid="visa-freedom-view" className="space-y-6">
      <div className="card p-6 lg:p-8 flex flex-col sm:flex-row sm:items-end gap-4">
        <div className="flex-1">
          <label className="overline mb-2 block">Select your passport</label>
          <select
            data-testid="passport-select"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="input-field text-base"
          >
            {passports.map((p) => (
              <option key={p.code} value={p.code}>{p.flag} {p.name}</option>
            ))}
          </select>
        </div>
        <div className="text-sm text-[#475569] sm:max-w-sm">
          See which countries accept your passport visa-free, with visa-on-arrival, or via eVisa.
        </div>
      </div>

      {data && (
        <>
          <div className="card p-8" style={{ background: "#0F172A" }}>
            <div className="flex items-center gap-3 mb-3" style={{ color: "#ffffff" }}>
              <Globe2 className="w-6 h-6" strokeWidth={1.5} style={{ color: "#ffffff" }} />
              <div className="font-display text-2xl font-bold tracking-tight" style={{ color: "#ffffff" }}>
                <span style={{ marginRight: "8px" }}>{data.passport.flag}</span>
                <span style={{ color: "#ffffff" }}>{data.passport.name} Passport</span>
              </div>
            </div>
            <p className="text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.85)" }}>{data.notes}</p>
          </div>

          <FreedomBucket
            title="Visa-Free"
            color="pill-do"
            countries={data.visa_free}
            description="Walk in with just your passport and onward ticket."
            testId="bucket-visa-free"
          />
          <FreedomBucket
            title="Visa on Arrival"
            color="pill-law"
            countries={data.visa_on_arrival}
            description="Apply at the airport on landing — pay fee, provide photo/passport."
            testId="bucket-voa"
          />
          <FreedomBucket
            title="eVisa / ETA"
            color="pill-culture"
            countries={data.evisa}
            description="Apply online before travel; usually approved within days."
            testId="bucket-evisa"
          />

          <div className="card p-6 bg-[#FFF7ED] border-l-4 border-l-[#FF3B30] text-sm text-[#0F172A] leading-relaxed">
            <strong>Note:</strong> Visa rules change frequently. Always verify on the destination country's official embassy site
            before booking. Lists above are illustrative selections, not exhaustive.
          </div>
        </>
      )}
    </div>
  );
}

function FreedomBucket({ title, color, countries, description, testId }) {
  return (
    <div className="card p-8" data-testid={testId}>
      <div className="flex items-center gap-3 mb-2 flex-wrap">
        <span className={`pill ${color}`}>{title}</span>
        <span className="text-sm text-[#475569]">{countries.length} destinations</span>
      </div>
      <p className="text-sm text-[#475569] mb-5">{description}</p>
      <div className="flex flex-wrap gap-2">
        {countries.map((c, i) => (
          <span key={i} className="bg-[#F1F5F9] border border-[#E2E8F0] rounded-sm px-3 py-1.5 text-sm text-[#0F172A]">
            {c}
          </span>
        ))}
      </div>
    </div>
  );
}

function InfoRow({ Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-4 h-4 text-[#002FA7] mt-1 shrink-0" strokeWidth={1.5} />
      <div className="min-w-0">
        <div className="overline mb-0.5">{label}</div>
        <div className="text-sm text-[#0F172A] leading-relaxed">{value}</div>
      </div>
    </div>
  );
}
